export const LINEAR_BUFFER_DEFAULT = 100;
export const LINEAR_BUFFER_MIN = 50;
export const LINEAR_BUFFER_MAX = 10000;

const METERS_PER_DEG_LAT = 111320;
const CORRIDOR_CAP_STEPS = 14;

const offsetMeters = (
  point: google.maps.LatLngLiteral,
  meters: number,
  headingRad: number
): google.maps.LatLngLiteral => {
  const dLat = (meters * Math.cos(headingRad)) / METERS_PER_DEG_LAT;
  const dLng =
    (meters * Math.sin(headingRad)) /
    (METERS_PER_DEG_LAT * Math.cos((point.lat * Math.PI) / 180));
  return { lat: point.lat + dLat, lng: point.lng + dLng };
};

const segmentHeading = (
  a: google.maps.LatLngLiteral,
  b: google.maps.LatLngLiteral
) => {
  const midLat = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  return Math.atan2((b.lng - a.lng) * Math.cos(midLat), b.lat - a.lat);
};

const vertexHeadings = (path: google.maps.LatLngLiteral[]) =>
  path.map((_, i) => {
    if (i === 0) return segmentHeading(path[0], path[1]);
    if (i === path.length - 1)
      return segmentHeading(path[path.length - 2], path[path.length - 1]);
    return segmentHeading(path[i - 1], path[i + 1]);
  });

const capArc = (
  center: google.maps.LatLngLiteral,
  radius: number,
  startHeading: number
) =>
  Array.from({ length: CORRIDOR_CAP_STEPS + 1 }, (_, i) =>
    offsetMeters(
      center,
      radius,
      startHeading - (Math.PI * i) / CORRIDOR_CAP_STEPS
    )
  );

export const buildCorridorRing = (
  path: google.maps.LatLngLiteral[],
  radius: number
): google.maps.LatLngLiteral[] => {
  if (path.length < 2 || radius <= 0) return [];

  const headings = vertexHeadings(path);
  const rightSide = path.map((p, i) =>
    offsetMeters(p, radius, headings[i] + Math.PI / 2)
  );
  const leftSide = path
    .map((p, i) => offsetMeters(p, radius, headings[i] - Math.PI / 2))
    .reverse();

  const lastIdx = path.length - 1;
  const endCap = capArc(path[lastIdx], radius, headings[lastIdx] + Math.PI / 2);
  const startCap = capArc(path[0], radius, headings[0] - Math.PI / 2);

  return [...rightSide, ...endCap, ...leftSide, ...startCap];
};

export const resolveLinearAxis = (
  dense: { latitude: number; longitude: number }[] | undefined,
  endpoints: { latitude: number; longitude: number }[]
): google.maps.LatLngLiteral[] => {
  const toLatLng = (coords: { latitude: number; longitude: number }[]) =>
    coords.map((c) => ({ lat: Number(c.latitude), lng: Number(c.longitude) }));

  const denseAxis = Array.isArray(dense) ? toLatLng(dense) : [];
  return denseAxis.length >= 2 ? denseAxis : toLatLng(endpoints);
};
